import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { GetImageUrl, GetSrcSetString } from 'helpers/getImageUrl';
import styles from './Image.module.scss';
import config from 'helpers/imageSizes';
import { inView } from 'helpers/inView';

export interface ImageProps extends Umbraco.Image {
	sizes: string;
	lazyload?: boolean;
	objectFitFallback?: boolean;
	isCover?: boolean;
	aspectRatio?: number;
	className?: string;
}

export const Image: React.FC<ImageProps> = ({
	url,
	focalPoint,
	properties,
	sizes,
	lazyload = true,
	objectFitFallback = true,
	isCover = true,
	aspectRatio,
	className,
}) => {
	const { thumbnailSize, fallbackSize } = config.images;

	const [src, setSrc] = useState(GetImageUrl({ url, width: thumbnailSize }));
	const [srcSet, setSrcSet] = useState(GetImageUrl({ url, width: thumbnailSize }));
	const [isFallback, setIsFallback] = useState(false);

	const imageRef = useRef(null);

	useEffect(() => {
		if (objectFitFallback) objectFit();
		if (lazyload) inView({ elm: imageRef.current, callback: reveal });
		if (!lazyload) reveal();
	}, []);

	const reveal = () => {
		const srcSetString = GetSrcSetString({ url, aspectRatio, focalPoint });
		setSrcSet(srcSetString);

		const srcString = GetImageUrl({ url, width: fallbackSize });
		setSrc(srcString);
	};

	const objectFit = () => {
		// If the useragent doesn't support ObjectFit (IE), we need to
		// add fallback image to the picture element
		const supportObjectFit = 'objectFit' in document.documentElement.style;

		if (!supportObjectFit) setIsFallback(true);
	};

	return (
		<span
			ref={imageRef}
			className={classNames(styles.Image, { [styles.isFallback]: isFallback }, { [styles.Image___cover]: isCover }, className)}
			style={isFallback ? { backgroundImage: `url(${src})` } : {}}
		>
			{!isFallback && <img className={styles.Image_asset} src={src} srcSet={srcSet} sizes={sizes} alt={properties.altText} />}
		</span>
	);
};
